import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import axios from 'axios';
import whobanner from "../img/contact.png";

import { Mail, Phone, MapPin } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* Leaflet marker fix */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});
const LOCATIONS = {
  INDIA: {
    label: "India Office – Hyderabad",
    position: [17.447764, 78.361649],
    zoom: 17,
  },
  UK: {
    label: "UK Office – Coventry",
    position: [52.4082, -1.4956],
    zoom: 17,
  },
};
const PINNED_BOUNDS = [
  [17.447764, 78.361649], // India
  [52.4082, -1.4956],    // UK
];
const StaticPinnedView = ({ onReady }) => {
  const map = useMap();
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current) return;

    // 1️⃣ Set pinned bounds (no animation)
    map.fitBounds(
      [
        [17.447764, 78.361649], // India
        [52.4082, -1.4956],    // UK
      ],
      {
        padding: [80, 80],
        animate: false,
      }
    );

    // 2️⃣ 🔥 FORCE TILE RENDER (THIS FIXES GRAY MAP)
    setTimeout(() => {
      map.invalidateSize();
    }, 0);

    applied.current = true;
    onReady(map);
  }, [map, onReady]);

  return null;
};


/* ✅ AUTO-FIT INDIA + UK ON LOAD */
const InitialPinnedView = ({ onReady }) => {
  const map = useMap();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;

    // Show both pins precisely
    const bounds = L.latLngBounds([
      LOCATIONS.INDIA.position,
      LOCATIONS.UK.position,
    ]);

    map.flyToBounds(bounds, {
      padding: [60, 60],
      duration: 1.5,
    });

    done.current = true;
    onReady(map);
  }, [map, onReady]);

  return null;
};



const Contact = () => {
  const mapRef = useRef(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+91',
    phoneno: '',
    location: '',
    purpose: '',
    message: '',
  });

  const [formMessage, setFormMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll states
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const RADIUS = 49;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset = CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;


  useEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Force scroll to top on component mount with multiple attempts
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (window.scrollY !== 0) {
        // If still not at top, try again after a short delay
        setTimeout(forceScrollToTop, 50);
      }
    };
    
    forceScrollToTop();
    
    // Additional fallback for stubborn browsers
    setTimeout(forceScrollToTop, 100);
    setTimeout(forceScrollToTop, 300);
    
    AOS.init({ duration: 1000, once: true });
    
    // Google Tag Manager tracking
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'page_view',
        page_title: 'Contact Us',
        page_location: window.location.href,
      });
    }
    
    // Meta Pixel tracking
    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'PageView', {
        page_title: 'Contact Us'
      });
    }
    
    return () => {
      // Restore scroll restoration when component unmounts
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Scroll progress logic
  useEffect(() => {
    const handleScroll = () => {
      const sc = window.scrollY || window.pageYOffset;
      setShowScroll(sc > 300);
      
      const doc = document.documentElement;
      const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
      const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;
      
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

const nameRegex = /^[A-Za-z\s]+$/;

const handleChange = (e) => {
  const { name, value } = e.target;

  // ✅ Name: only alphabets & spaces
  if (name === "name") {
    if (!/^[A-Za-z\s]*$/.test(value)) return;
  }

  setFormData((prev) => ({ ...prev, [name]: value }));
};


  const isValidPhoneNumber = () => {
    try {
      const phone = parsePhoneNumberFromString(
        `${formData.country_code}${formData.phoneno}`
      );
      return phone?.isValid();
    } catch {
      return false;
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  // ✅ phone validation
  if (!isValidPhoneNumber()) {
    setFormMessage("Please enter a valid phone number.");
    setMessageType("error");
    setIsSubmitting(false);
    return;
  }

  // ✅ Name validation
  if (!nameRegex.test(formData.name)) {
    setFormMessage("Name should contain only alphabets.");
    setMessageType("error");
    setIsSubmitting(false);
    return;
  }

  try {
    await axios.post(
      "https://api.securxperts.com:8000/forms/contact",
      formData,
      { headers: { "Content-Type": "application/json" } }
    );

    // ✅ SUCCESS - Redirect to thank you page
    navigate('/thank-you', { state: { formType: 'contact' } });

    // reset form
    setFormData({
      name: "",
      email: "",
      country_code: "+91",
      phoneno: "",
      location: "",
      purpose: "",
      message: "",
    });
  } catch (error) {
    // ❌ ERROR
    setFormMessage("Please Enter Valid Details.");
    setMessageType("error");
  } finally {
    setIsSubmitting(false);
  }
};

  

  return (
  <div className="min-h-screen bg-gray-50 font-sans relative z-0">
      <Navbar />

      <header className="relative w-full">
        <div
          className="relative w-full h-[317px] bg-cover bg-center"
          style={{ backgroundImage: `url(${whobanner})` }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
            <h1 className="text-4xl font-bold">Contact Us</h1>
            <div className="flex items-center gap-2">
              <Link to="/" className="hover:underline">Home</Link>
             <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-[2px]"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Contact Us</span>
            </div>
          </div>
        </div>
      </header>

      <section className="py-20">
        <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-xl flex flex-col lg:flex-row">

          {/* LEFT */}
          <div className="bg-[#1b1b1b] text-white p-10 lg:w-5/12 space-y-6">
            <h2 className="text-3xl font-bold">Contact Information</h2>

            <p className="flex items-center"><Phone className="mr-2" /> +91 79932 56679</p>
            <p className="flex items-center"><Mail className="mr-2" /> info@SecurXperts.com</p>
{/* ADDRESSES */}
<div className="space-y-6 text-sm leading-relaxed">
  <div className="flex items-start gap-3">
    <MapPin className="mt-1 text-blue-400" />
    <div>
      <p className="font-semibold">India Office:</p>
      <p>3rd Floor, PR R One Towers</p>
                      <p>Plot No 59, DLF Rd, near Radisson Hotel</p>
                      <p>Jayabheri Enclave, Gachibowli, Hyderabad, Telangana 500032</p>
      
    </div>
  </div>

  <div className="border-t border-white/20 pt-6 flex items-start gap-3">
    <MapPin className="mt-1 text-blue-400" />
    <div>
      <p className="font-semibold">UK Office:</p>
      <p>194 Gulson Road,</p>
      <p>Coventry, England, CV1 2JD</p>
      <p>United Kingdom</p>
    </div>
  </div>
</div>

            {/* MAP */}
            <div className="relative mt-6 h-[280px] rounded-xl overflow-hidden bg-[#223648]">

              <div className="absolute top-3 left-3 z-[10] bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow">
                📍 Our Global Offices
              </div>

              <button
                className="absolute bottom-14 right-4 z-[10] bg-blue-600 text-white px-3 py-1.5 rounded-lg"
                onClick={() => mapRef.current?.flyTo([17.447764, 78.361649], 17)}
              >
                📍 India
              </button>

              <button
                className="absolute bottom-4 right-4 z-[10] bg-blue-600 text-white px-3 py-1.5 rounded-lg"
                onClick={() => mapRef.current?.flyTo([52.4082, -1.4956], 17)}
              >
                📍 UK
              </button>

        <MapContainer
 
 center={[20, 0]}
  zoom={2}
  zoomControl={false}
  scrollWheelZoom={false}
  dragging={true}
  style={{ height: "280px", width: "100%" }}
  className="absolute inset-0 rounded-xl z-0"
>

               <TileLayer
    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    attribution="&copy; OpenStreetMap contributors"
    noWrap
  />
  <StaticPinnedView onReady={(map) => (mapRef.current = map)} />

                {/* <FitWorldBounds /> */}

                <Marker position={[17.447764, 78.361649]}>
                  <Popup>India Office – Hyderabad</Popup>
                </Marker>

                <Marker position={[52.4082, -1.4956]}>
                  <Popup>UK Office – Coventry</Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>

          {/* RIGHT FORM */}
         {/* RIGHT FORM */}
<div className="p-10 lg:w-7/12">
  <form onSubmit={handleSubmit} className="space-y-6">

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Full Name <span className="text-red-500">*</span>
      </label>
      <input
        name="name"
        value={formData.name}
        onChange={handleChange}
        placeholder="Enter your full name"
        required
        className="w-full border-b-2 pb-2 pl-3"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Email <span className="text-red-500">*</span>
      </label>
      <input
        name="email"
        value={formData.email}
        onChange={handleChange}
        placeholder="Enter your email address"
        maxLength={50}
        required
        className="w-full border-b-2 pb-2 pl-3"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Phone Number <span className="text-red-500">*</span>
      </label>
      <PhoneInput
        country="in"
        value={`${formData.country_code.replace('+', '')}${formData.phoneno}`}
        onChange={(value, country) => {
          setFormData(prev => ({
            ...prev,
            country_code: `+${country.dialCode}`,
            phoneno: value.slice(country.dialCode.length),
          }));
        }}
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Location <span className="text-red-500">*</span>
      </label>
      <input
        name="location"
        value={formData.location}
        onChange={handleChange}
        placeholder="Enter your location"
        required
        className="w-full border-b-2 pb-2 pl-3"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Purpose <span className="text-red-500">*</span>
      </label>
      <select
        name="purpose"
        value={formData.purpose}
        onChange={handleChange}
        required
        className="w-full border-b-2 pb-2 pl-3 bg-transparent"
      >
        <option value="">Select Purpose</option>
        <option value="Business Enquiry">Business Enquiry</option>
        <option value="Partnership">Partnership</option>
        <option value="Support">Support</option>
        <option value="Careers">Careers</option>
        <option value="Other">Other</option>
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Message
      </label>
      <textarea
        name="message"
        value={formData.message}
        onChange={handleChange}
        placeholder="Enter your message (optional)"
        rows="4"
        className="w-full border-b-2 pb-2 pl-3"
      />
    </div>

    <button
      type="submit"
      disabled={isSubmitting}
      className="bg-indigo-900 text-white px-10 py-3 rounded-lg hover:bg-indigo-800 disabled:opacity-50"
    >
      {isSubmitting ? 'Sending...' : 'Submit'}
    </button>

  </form>
  {/* ✅ FORM STATUS MESSAGE AT BOTTOM */}
{/* {formMessage && (
  <div className="max-w-6xl mx-auto mt-12 px-4">
    <div
      className={`text-center p-4 rounded-lg font-semibold mt-12 ${
        messageType === 'success'
          ? 'bg-green-100 text-green-700 border border-green-300'
          : 'bg-red-100 text-red-700 border border-red-300'
      }`}
    >
      {formMessage}
    </div>
  </div>
)} */}

</div>

        </div>
        

      </section>

      {/* Scroll to Top Button */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>

          <svg className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Contact;
